import { get, post, put } from "@sal/webrequest-web";
import { assertApiSuccess } from "code/apiError";
import { FleetVehicle } from "models/FleetVehicle";
import { FleetVehicleType } from "models/FleetVehicleType";
import { useMemo } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";

export type UpsertVehicleRequest = {
	registration: string;
	fleetVehicleTypeId: number;
	lengthM: number;
	refrigerated: string;
	driverContactId?: number | null;
	tractorRego?: string;
	trailer1Rego?: string;
	trailer2Rego?: string;
	status: string;
};

type MutationResult = { success: true } | { success: false; errorMessage: string };

const fetcher = async (url: string) =>
	assertApiSuccess(await get(url), "Failed to load fleet vehicles.");

const options = {
	revalidateIfStale: false,
	revalidateOnFocus: false,
	revalidateOnReconnect: false
};

const createVehicleFetcher = async (_key: string, { arg }: { arg: UpsertVehicleRequest }) => {
	assertApiSuccess(await post("api/Fleet/CreateFleetVehicle", arg), "Failed to create vehicle.");
};

const updateVehicleFetcher = async (
	_key: string,
	{ arg }: { arg: { id: number; payload: UpsertVehicleRequest } }
) => {
	assertApiSuccess(
		await put(`api/Fleet/UpdateFleetVehicle/${arg.id}`, arg.payload),
		"Failed to update vehicle."
	);
};

export function useCreateVehicle() {
	const { trigger: triggerCreateVehicle, isMutating: isCreating } = useSWRMutation(
		"api/Fleet/List",
		createVehicleFetcher
	);

	const createVehicle = async (payload: UpsertVehicleRequest): Promise<MutationResult> => {
		try {
			await triggerCreateVehicle(payload);
			return { success: true } as const;
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Failed to create vehicle.";
			console.error("Failed to create vehicle", error);
			return { success: false, errorMessage } as const;
		}
	};

	return { createVehicle, isCreating };
}

export function useGetFleetVehicles(enabled = true) {
	const {
		data,
		isLoading,
		error,
		isValidating,
		mutate: mutateData
	} = useSWR(enabled ? "api/Fleet/List" : null, fetcher, options);
	const rows = useMemo(() => (data as FleetVehicle[]) ?? [], [data]);

	return useMemo(
		() => ({
			data: rows,
			isDataLoading: isLoading,
			dataError: error,
			dataValidating: isValidating,
			dataEmpty: !isLoading && rows.length === 0,
			mutateData
		}),
		[rows, error, isLoading, isValidating, mutateData]
	);
}

export function useUpdateVehicle() {
	const { trigger: triggerUpdateVehicle, isMutating: isUpdating } = useSWRMutation(
		"api/Fleet/List",
		updateVehicleFetcher
	);

	const updateVehicle = async (
		id: number,
		payload: UpsertVehicleRequest
	): Promise<MutationResult> => {
		try {
			await triggerUpdateVehicle({ id, payload });
			return { success: true } as const;
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Failed to update vehicle.";
			console.error("Failed to update vehicle", error);
			return { success: false, errorMessage } as const;
		}
	};

	return { updateVehicle, isUpdating };
}

type FleetMutationResult = { success: true } | { success: false; errorMessage: string };

const deactivateVehicleFetcher = async (_key: string, { arg }: { arg: number }) => {
	assertApiSuccess(
		await post(`api/Fleet/DeactivateFleetVehicle/${arg}`, {}),
		"Failed to deactivate vehicle."
	);
};

export function useDeactivateFleetVehicle() {
	const { trigger: triggerDeactivate, isMutating: isDeactivating } = useSWRMutation(
		"api/Fleet/List",
		deactivateVehicleFetcher
	);

	const deactivateFleetVehicle = async (vehicleId: number): Promise<FleetMutationResult> => {
		try {
			await triggerDeactivate(vehicleId);
			return { success: true } as const;
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Failed to deactivate vehicle.";
			console.error("Failed to deactivate fleet vehicle", error);
			return { success: false, errorMessage } as const;
		}
	};

	return { deactivateFleetVehicle, isDeactivating };
}

const activateVehicleFetcher = async (_key: string, { arg }: { arg: number }) => {
	assertApiSuccess(
		await post(`api/Fleet/ActivateFleetVehicle/${arg}`, {}),
		"Failed to activate vehicle."
	);
};

export function useActivateFleetVehicle() {
	const { trigger: triggerActivate, isMutating: isActivating } = useSWRMutation(
		"api/Fleet/List",
		activateVehicleFetcher
	);

	const activateFleetVehicle = async (vehicleId: number): Promise<FleetMutationResult> => {
		try {
			await triggerActivate(vehicleId);
			return { success: true } as const;
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Failed to activate vehicle.";
			console.error("Failed to activate fleet vehicle", error);
			return { success: false, errorMessage } as const;
		}
	};

	return { activateFleetVehicle, isActivating };
}

export type BulkStatusUpdateFailure = { id: number; error: string };
export type BulkStatusUpdateResult = { succeeded: number[]; failed: BulkStatusUpdateFailure[] };

export async function bulkDeactivateFleetVehiclesApi(
	ids: number[]
): Promise<BulkStatusUpdateResult> {
	return assertApiSuccess<BulkStatusUpdateResult>(
		await post("api/Fleet/BulkDeactivateVehicles", { ids }),
		"Failed to deactivate vehicles."
	);
}

export async function bulkActivateFleetVehiclesApi(ids: number[]): Promise<BulkStatusUpdateResult> {
	return assertApiSuccess<BulkStatusUpdateResult>(
		await post("api/Fleet/BulkActivateVehicles", { ids }),
		"Failed to activate vehicles."
	);
}

export function useGetFleetVehicleTypes(enabled = true) {
	const {
		data,
		isLoading,
		error,
		isValidating,
		mutate: mutateData
	} = useSWR(enabled ? "api/Fleet/GetFleetVehicleTypes" : null, fetcher, options);
	const rows = useMemo(() => (data as FleetVehicleType[]) ?? [], [data]);

	return useMemo(
		() => ({
			data: rows,
			isDataLoading: isLoading,
			dataError: error,
			dataValidating: isValidating,
			dataEmpty: !isLoading && rows.length === 0,
			mutateData
		}),
		[rows, error, isLoading, isValidating, mutateData]
	);
}
